import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronRight, RefreshCw } from "lucide-react";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { EmptyState, ErrorState, LoadingState, PageHeading } from "@/components/app/AppUI";
import { campanasRepository } from "@/data/repositories";
import {
  ETIQUETA_PERIODO,
  agruparCampanas,
  nombreFila,
  rangoPeriodo,
  textoHace,
  textoRoas,
  type CampanaConAnuncios,
  type FilaCampana,
  type PeriodoCampanas,
} from "@/domain/campanas";
import { textoImporte } from "@/domain/cobros";

export const Route = createFileRoute("/_authenticated/app/campanas")({ component: CampanasPage });

const PERIODOS: readonly PeriodoCampanas[] = ["7d", "30d", "mes", "todo"];

/** Columnas de la tabla: nombre y, a la derecha, las cifras. */
const COLUMNAS = "grid-cols-[minmax(12rem,1.5fr)_repeat(10,minmax(4.5rem,1fr))]";

function importe(cents: number | null): string {
  return cents === null ? "–" : textoImporte(cents);
}

/** Del gasto en Meta a la boda reservada, por campaña y por anuncio. */
function CampanasPage() {
  const [periodo, setPeriodo] = useState<PeriodoCampanas>("30d");
  const rango = rangoPeriodo(periodo, new Date());
  const queryClient = useQueryClient();

  const datos = useQuery({
    queryKey: ["campanas", rango.desde, rango.hasta],
    queryFn: () => campanasRepository.resumen(rango),
  });
  const ultima = useQuery({
    queryKey: ["campanas-sync"],
    queryFn: () => campanasRepository.ultimaSincronizacion(),
    refetchInterval: 60_000,
  });

  const sincronizar = useMutation({
    mutationFn: () => campanasRepository.sincronizarAhora(),
    onSuccess: (resultado) => {
      queryClient.invalidateQueries({ queryKey: ["campanas"] });
      queryClient.invalidateQueries({ queryKey: ["campanas-sync"] });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
      if (resultado.errores.length > 0) toast.error(resultado.errores[0]);
      else
        toast.success(
          resultado.leadsNuevos === 1
            ? "Sincronizado · 1 lead nuevo"
            : `Sincronizado · ${resultado.leadsNuevos} leads nuevos`,
        );
    },
    onError: (error) => toast.error(error.message),
  });

  const resumen = agruparCampanas(datos.data ?? []);
  const errores = ultima.data?.errores ?? [];

  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Ventas · Meta"
        title="Campañas"
        description="Lo que cuesta cada lead y cada boda reservada, cruzando el gasto de Meta con el CRM."
        action={
          <div className="flex flex-col items-start gap-2 md:items-end">
            <button
              type="button"
              className="btn-primary"
              onClick={() => sincronizar.mutate()}
              disabled={sincronizar.isPending}
            >
              <RefreshCw className={`size-4 ${sincronizar.isPending ? "animate-spin" : ""}`} />
              {sincronizar.isPending ? "Sincronizando" : "Sincronizar ahora"}
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {ultima.data
                ? `Última sincronización ${textoHace(ultima.data.at, new Date())}`
                : "Aún no se ha sincronizado"}
            </p>
          </div>
        }
      />

      {errores.length > 0 && (
        <div className="flex items-start gap-3 border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <p className="font-semibold">La última sincronización tuvo avisos</p>
            {errores.slice(0, 3).map((error) => (
              <p key={error}>{error}</p>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Periodo">
        {PERIODOS.map((p) => (
          <button
            key={p}
            type="button"
            aria-pressed={periodo === p}
            onClick={() => setPeriodo(p)}
            className={`border px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
              periodo === p
                ? "border-foreground bg-foreground text-background"
                : "border-border bg-white text-muted-foreground hover:border-foreground hover:text-foreground"
            }`}
          >
            {ETIQUETA_PERIODO[p]}
          </button>
        ))}
      </div>

      {datos.isLoading ? (
        <LoadingState label="Cruzando campañas y bodas" />
      ) : datos.error ? (
        <ErrorState message={datos.error.message} />
      ) : !resumen.total ? (
        <section className="panel">
          <EmptyState
            title="Aún no hay datos de campañas"
            description="Aparecen cuando corre la sincronización con Meta: cada 10 minutos los leads y cada 6 horas el gasto. Con «Sincronizar ahora» se trae todo al momento."
          />
        </section>
      ) : (
        <>
          <ResumenTotal total={resumen.total} />
          <TablaCampanas campanas={resumen.campanas} />
        </>
      )}
    </div>
  );
}

function ResumenTotal({ total }: { total: FilaCampana }) {
  const cifras: { etiqueta: string; valor: string; destacado?: boolean }[] = [
    { etiqueta: "Gasto", valor: textoImporte(total.gasto_cents) },
    { etiqueta: "Leads", valor: String(total.leads_meta) },
    { etiqueta: "Coste por lead", valor: importe(total.coste_lead_cents) },
    { etiqueta: "Reservas", valor: String(total.reservados) },
    { etiqueta: "Coste por boda", valor: importe(total.coste_boda_cents), destacado: true },
    { etiqueta: "Contratado", valor: textoImporte(total.contratado_cents) },
    { etiqueta: "ROAS", valor: textoRoas(total.roas) },
  ];
  return (
    <section className="grid grid-cols-2 gap-px border border-border bg-border sm:grid-cols-4 xl:grid-cols-7">
      {cifras.map((cifra) => (
        <div
          key={cifra.etiqueta}
          className={cifra.destacado ? "bg-onyx px-5 py-4 text-bone" : "bg-white px-5 py-4"}
        >
          <p className={`eyebrow ${cifra.destacado ? "!text-neon" : ""}`}>{cifra.etiqueta}</p>
          <p className="mt-1 font-display text-3xl tracking-[-0.04em]">{cifra.valor}</p>
        </div>
      ))}
    </section>
  );
}

function TablaCampanas({ campanas }: { campanas: CampanaConAnuncios[] }) {
  const [abiertas, setAbiertas] = useState<Set<string>>(() => new Set());
  const alternar = (id: string) =>
    setAbiertas((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });

  return (
    <section className="panel overflow-x-auto">
      <div className="min-w-[66rem]">
        <div
          className={`data-row ${COLUMNAS} font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground hover:!bg-transparent`}
        >
          <span>Campaña · anuncio</span>
          <span className="text-right">Gasto</span>
          <span className="text-right">Leads</span>
          <span className="text-right">En CRM</span>
          <span className="text-right">Contactados</span>
          <span className="text-right">Propuestas</span>
          <span className="text-right">Reservas</span>
          <span className="text-right">Coste / lead</span>
          <span className="text-right">Coste / boda</span>
          <span className="text-right">Contratado</span>
          <span className="text-right">ROAS</span>
        </div>
        {campanas.map(({ campana, anuncios }) => {
          const id = campana.campaign_id ?? "sin-campana";
          const abierta = abiertas.has(id);
          return (
            <Fragment key={id}>
              <button
                type="button"
                className={`data-row w-full text-left ${COLUMNAS}`}
                onClick={() => alternar(id)}
                aria-expanded={abierta}
              >
                <span className="flex items-center gap-2 font-semibold">
                  <ChevronRight
                    className={`size-4 shrink-0 text-muted-foreground transition-transform ${abierta ? "rotate-90" : ""}`}
                  />
                  <span className="truncate" title={nombreFila(campana)}>
                    {nombreFila(campana)}
                  </span>
                  <span className="font-mono text-[10px] font-normal text-muted-foreground">
                    {anuncios.length}
                  </span>
                </span>
                <Cifras fila={campana} />
              </button>
              {abierta &&
                anuncios.map((anuncio) => (
                  <div
                    key={anuncio.ad_id ?? "sin-anuncio"}
                    className={`data-row bg-secondary/40 text-sm ${COLUMNAS}`}
                  >
                    <span
                      className="truncate pl-6 text-muted-foreground"
                      title={nombreFila(anuncio)}
                    >
                      {nombreFila(anuncio)}
                    </span>
                    <Cifras fila={anuncio} />
                  </div>
                ))}
            </Fragment>
          );
        })}
      </div>
    </section>
  );
}

function Cifras({ fila }: { fila: FilaCampana }) {
  const valores = [
    textoImporte(fila.gasto_cents),
    String(fila.leads_meta),
    String(fila.leads_crm),
    String(fila.contactados),
    String(fila.propuestas),
    String(fila.reservados),
    importe(fila.coste_lead_cents),
    importe(fila.coste_boda_cents),
    textoImporte(fila.contratado_cents),
    textoRoas(fila.roas),
  ];
  return valores.map((valor, i) => (
    <span key={i} className="text-right tabular-nums">
      {valor}
    </span>
  ));
}
